import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowUpRight, Linkedin, Globe2 } from "lucide-react";
import { navLinks } from "../../mock";
import { useContent } from "../../context/ContentContext";
import { useRFQ } from "../../context/RFQContext";
import Logo from "../Logo";

export default function Footer() {
  const { openRFQ } = useRFQ();
  const { content } = useContent();
  const company = content.company || {};
  const services = content.services || [];
  const productCategories = content.productCategories || [];
  const year = new Date().getFullYear();

  return (
    <footer className="bg-[#06111d] text-white/80">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10">
        {/* CTA strip */}
        <div className="rounded-2xl brand-gradient p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6 mb-14">
          <div>
            <h3 className="font-display text-2xl md:text-3xl text-white leading-tight">
              Ready to source your next product?
            </h3>
            <p className="text-white/75 mt-2 text-sm max-w-xl">
              Let's work together to bring quality medicines to patients worldwide.
            </p>
          </div>
          <button
            onClick={openRFQ}
            className="shrink-0 inline-flex items-center gap-2 bg-[#d9a441] hover:bg-[#c8942f] text-[#0c1626] font-semibold rounded-full px-6 h-12 transition-colors"
          >
            Request an RFQ <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8">
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="h-10 w-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Logo size={28} />
              </span>
              <span className="leading-none">
                <span className="block font-display text-lg text-white">Novocreste</span>
                <span className="block text-[10px] uppercase tracking-[0.22em] text-[#7fc6d6]">International</span>
              </span>
            </Link>
            <p className="text-sm text-white/60 mt-4 max-w-xs leading-relaxed">
              {company.legalName} — a global pharmaceutical sourcing partner connecting healthcare companies with a trusted manufacturing network.
            </p>
            <div className="mt-5 space-y-2 text-sm">
              <a href={`mailto:${company.email}`} className="flex items-center gap-2 py-2 md:py-0 hover:text-[#7fc6d6] transition-colors">
                <Mail className="h-4 w-4 text-[#4fa9c0]" /> {company.email}
              </a>
              <a href={`tel:${company.phoneHref}`} className="flex items-center gap-2 py-2 md:py-0 hover:text-[#7fc6d6] transition-colors">
                <Phone className="h-4 w-4 text-[#4fa9c0]" /> {company.phone}
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-4">Company</p>
            <ul className="space-y-0 md:space-y-2.5 text-sm">
              {navLinks.filter((l) => l.to !== "/").map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-block py-2.5 md:py-0 hover:text-[#7fc6d6] transition-colors">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-4">Services</p>
            <ul className="space-y-0 md:space-y-2.5 text-sm">
              {services.slice(0, 6).map((s) => (
                <li key={s.title}>
                  <Link to="/services" className="inline-block py-2.5 md:py-0 hover:text-[#7fc6d6] transition-colors">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-white/40 mb-4">Products</p>
            <ul className="space-y-0 md:space-y-2.5 text-sm">
              {productCategories.slice(0, 6).map((c) => (
                <li key={c.name}>
                  <Link to="/products" className="inline-block py-2.5 md:py-0 hover:text-[#7fc6d6] transition-colors">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">© {year} {company.legalName}. All rights reserved.</p>
          <div className="flex items-center gap-4 text-xs text-white/40">
            <span className="inline-flex items-center gap-1.5"><Globe2 className="h-3.5 w-3.5" /> Serving 50+ countries</span>
            <a href={`mailto:${company.email}`} className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors">
              <Linkedin className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
